// Períodos mensais das telas de Finanças (extrato, lançamentos): mês corrente por padrão, atalhos
// "mês anterior / atual / próximo" e rótulo pt-BR. Tudo em strings "AAAA-MM-DD" e componentes
// numéricos — sem `new Date('AAAA-MM-DD')` (ver utils/formatters.ts).
import { pad2 } from '@components/dateInputUtils';

/** Mês de calendário: `month` 1..12. */
export interface YearMonth {
  year: number;
  month: number;
}

export interface DateRange {
  start: string;
  end: string;
}

const MONTH_NAMES = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];

/** Primeiro e último dia do mês (`month` 1..12) como "AAAA-MM-DD". */
export const monthRange = (year: number, month: number): DateRange => {
  const last = new Date(year, month, 0).getDate();
  return { start: `${year}-${pad2(month)}-01`, end: `${year}-${pad2(month)}-${pad2(last)}` };
};

/** Soma `delta` meses (pode ser negativo) a um mês de calendário. */
export const shiftMonth = ({ year, month }: YearMonth, delta: number): YearMonth => {
  const index = year * 12 + (month - 1) + Math.trunc(delta);
  const y = Math.floor(index / 12);
  return { year: y, month: index - y * 12 + 1 };
};

/** "novembro de 2026". */
export const monthLabel = ({ year, month }: YearMonth): string => `${MONTH_NAMES[month - 1] ?? '?'} de ${year}`;

/** Mês de uma data "AAAA-MM-DD" (ou carimbo); null se inválida. */
export const monthOf = (iso: string): YearMonth | null => {
  const m = /^(\d{4})-(\d{2})/.exec(iso);
  if (!m) return null;
  const month = Number(m[2]);
  if (month < 1 || month > 12) return null;
  return { year: Number(m[1]), month };
};

/** Mês corrente pelo relógio local do navegador. */
export const currentMonth = (now: Date = new Date()): YearMonth => ({ year: now.getFullYear(), month: now.getMonth() + 1 });

/**
 * Se o intervalo cobre exatamente um mês de calendário (1º ao último dia), devolve esse mês;
 * senão null. Usado para mostrar o rótulo "novembro de 2026" e habilitar os atalhos de mês.
 */
export const rangeAsMonth = ({ start, end }: DateRange): YearMonth | null => {
  const ym = monthOf(start);
  if (!ym) return null;
  const r = monthRange(ym.year, ym.month);
  return r.start === start && r.end === end ? ym : null;
};

/** Rótulo do intervalo: "novembro de 2026" quando é um mês inteiro; senão "dd/MM/aaaa a dd/MM/aaaa". */
export const rangeLabel = (range: DateRange): string => {
  const ym = rangeAsMonth(range);
  if (ym) return monthLabel(ym);
  const br = (iso: string) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
    return m ? `${m[3]}/${m[2]}/${m[1]}` : iso;
  };
  return `${br(range.start)} a ${br(range.end)}`;
};
